import { useTheme } from "@emotion/react";
import React, { useEffect, useState } from "react";
import { LightTooltip } from "../../../../kit/LightToolTip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import { Button } from "@mui/material";
import { useStyles } from "../../../../theme/style";
import "./style.scss";
function RadioChips({
  label,
  type,
  radioList,
  value,
  isRequired = false,
  isDisabled = false,
  info = "",
  onChange,
  componentId,
  categoryId,
  dependentText = "",
  isEditable = false,
  downgradePossible = true,
  warning = "",
}) {
  
  const theme = useTheme();
  const classes = useStyles(theme);
  
  // State to maintain enabled buttons based on initial value
  const [enabledButtons, setEnabledButtons] = useState(new Set());
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Calculate enabled buttons based on initial value and downgradePossible
  const calculateEnabledButtons = (initialValue, downgradePossible, radioList) => {
    // Define the hierarchy: Small < Medium < Large
    const hierarchy = { 'Small': 1, 'Medium': 2, 'Large': 3 };
    const initialLevel = hierarchy[initialValue];
    
    const newEnabledButtons = new Set();
    
    // Always enable the initial value button
    newEnabledButtons.add(initialValue);
    
    // If downgradePossible is false, only allow upgrades (higher values) from initial value
    if (!downgradePossible) {
      radioList.forEach(chip => {
        const buttonLevel = hierarchy[chip.property_value];
        if (buttonLevel > initialLevel) {
          newEnabledButtons.add(chip.property_value);
        }
      });
    } else {
      // If downgradePossible is true, all buttons are enabled
      radioList.forEach(chip => {
        newEnabledButtons.add(chip.property_value);
      });
    }
    
    return newEnabledButtons;
  };
  
  // Initialize enabled buttons on first render
  useEffect(() => {
    if (!isInitialized) {
      const newEnabledButtons = calculateEnabledButtons(value, downgradePossible, radioList);
      setEnabledButtons(newEnabledButtons);
      setIsInitialized(true);
    }
  }, [value, downgradePossible, radioList, isInitialized]);
  
  // Update enabled buttons only when downgradePossible changes
  useEffect(() => {
    if (isInitialized) {
      const newEnabledButtons = calculateEnabledButtons(value, downgradePossible, radioList);
      setEnabledButtons(newEnabledButtons);
    }
  }, [downgradePossible, isInitialized, radioList]);
  
  // Function to determine if a button should be disabled
  const isButtonDisabled = (buttonValue) => {
    if (isDisabled) return true;
    return !enabledButtons.has(buttonValue);
  };
  
  // console.log("RadioChips props:", {
  //   label,
  //   type,
  //   radioList,
  //   value,
  //   isRequired,
  //   isDisabled,
  //   info,
  //   onChange,
  //   componentId,
  //   categoryId,
  //   dependentText,
  //   isEditable,
  //   downgradePossible,
  // });
  
  return (
    <div style={{ display: "flex" }}>
      {label !== "" && (
        <div style={{ marginRight: theme.size[16] }}>
          <div
            style={{
              fontSize: theme.size[12],
              fontWeight: theme.fontWeight[500],
              color: theme.palette.rapidInfra.text,
              width: "fit-content",
              whiteSpace: "nowrap",
            }}
          >
            {dependentText}
          </div>
          <div
            style={{
              fontSize: theme.size[12],
              fontWeight: theme.fontWeight[500],
              color: theme.palette.rapidInfra.text,
              width: "fit-content",
              whiteSpace: "nowrap",
            }}
          >
            {label}

            {isRequired && (
              <span style={{ color: "red", marginLeft: 5 }}>*</span>
            )}
            {info && (
              <LightTooltip
                title={info}
                placement="top"
                arrow
                sx={{
                  "& .MuiTooltip-tooltip": {
                    padding: "8px 12px", // Adjust padding if needed
                  },
                }}
              >
                <InfoOutlinedIcon
                  style={{
                    fontSize: theme.size[14],
                    color: theme.palette.rapidInfra.infoIcon,
                    marginLeft: 5,
                    cursor: "pointer",
                  }}
                />
              </LightTooltip>
            )}
            {isEditable && (
              <LightTooltip
                title={warning}
                placement="top"
                arrow
                sx={{
                  "& .MuiTooltip-tooltip": {
                    padding: "8px 12px", // Adjust padding if needed
                  },
                }}
              >
                <WarningIcon
                  style={{
                    fontSize: theme.size[14],
                    color: "#FF9800", // Orange color for warning
                    marginLeft: 5,
                    cursor: "pointer",
                  }}
                />
              </LightTooltip>
            )}

          </div>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center" }}>
        {radioList.map((curr_chip, index) => {
          return (
            <div key={index} style={{ marginRight: theme.size[12] }}>
              <Button
                disableRipple
                disabled={isButtonDisabled(curr_chip.property_value)}
                className={
                  value === curr_chip.property_value
                    ? classes.filledButton
                    : classes.outlinedButton
                }
                onClick={() => {
                  onChange(categoryId, componentId, curr_chip.property_value);
                }}
                style={{
                  fontWeight: theme.fontWeight[500],
                  marginRight: theme.size[8],
                  backgroundColor: isButtonDisabled(curr_chip.property_value)
                    ? value === curr_chip.property_value
                      ? theme.palette.rapidInfra.disabledGrey
                      : "white"
                    : "",
                }}
              >
                {curr_chip.property_value}
              </Button>

              <LightTooltip
                title={curr_chip.description}
                placement="top"
                arrow
                sx={{
                  "& .MuiTooltip-tooltip": {
                    padding: "8px 12px", // Adjust padding if needed
                  },
                }}
              >
                <div
                  style={{
                    fontSize: theme.size[12],
                    color: theme.palette.rapidInfra.text,
                    marginTop: theme.size[4],
                  }}
                  className="three-line-ellipsis"
                >
                  {/* {curr_chip.description} */}
                  {curr_chip.description
                    ?.split(",")
                    ?.map((item) => item.trim())
                    ?.map((part, index) => {
                      return <div>{part}</div>;
                    })}
                </div>
              </LightTooltip>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default RadioChips;
